import { cn } from '@invent/wl-ui-kit-next';
import { Input, Popover, PopoverContent, PopoverTrigger } from '@invent/wl-ui-kit-next/ui';
import { ChevronDown, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { HelpPopover, StatusChip } from '@/design';
import { useCan } from '@/domains/auth';

import type { IListField } from '../../api/lists-contracts';
import { originLabel, provenanceDraws, provenanceParts, provenanceSearchText } from '../../lists';
import { TEAM_PATHS } from '../../paths';

/**
 * Choosing a field of a list's catalogue (SR-403, the owner's ask of 2026-10-09; design
 * `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §3.13) — the columns sheet, a
 * report's Fields panel, a filter's condition, a formatting rule, a report's sort, *Set a field…*
 * and an export layout's column. A survey's catalogue runs to hundreds of fields — FA's answers
 * alone are some 165 on UAT — so every one of those draws it the same way:
 *
 * - **the catalogue's groups** (`catalogueSections` — Record, Profile, Survey questions, Staff
 *   fields…), each a heading with its count that **folds, and starts folded**: the groups are the
 *   map, and a group is opened to read it. The group holding the field already chosen opens on its
 *   own, so a press to change it lands where it is;
 * - **one search box** over a field's label and question number — every word typed must appear —
 *   which opens each group holding a match and leaves out the rest, a heading pressed during it
 *   folding that group for the search only.
 *
 * Nothing is grouped finer than survey-api's groups: an answer's survey section is not on the wire,
 * and a heading made from a code's segments would spell a code to a reader (the owner's ruling of
 * 2026-10-09). What is open is the press's, never stored — each opening starts folded.
 *
 * `FieldPicker` is the one-field form: a button naming the field, opening the list in a popover —
 * the search box focused, ↓ into the list, ↑/↓ through headings and fields, → and ← to unfold and
 * fold a heading, Enter to choose (from the box, the first match). Each group's fields are a
 * `listbox` of `option`s, so a choice reads as one. The columns editor draws the same groups and
 * search over its boxes, through `useFieldFolds` and `FieldGroupHeading`.
 *
 * It takes any groups of `{code, label, number}`, not the catalogue's alone: a ranking's scoring
 * template (SR-231) offers the staff form's groups and the survey's questions through it, with
 * `codes` on — the code drawn after each name and searched too, since that template is written in
 * codes.
 */

/** What the picker reads of a field — its provenance (SR-420) where the catalogue sends one. */
export type PickerField = Pick<IListField, 'code' | 'label' | 'number' | 'provenance' | 'hidden'>;

/** A group of fields as the picker draws it — `ICatalogueSection` is one. `survey` is the
 *  catalogue's, which a field's provenance line names. */
export interface IFieldSection {
  key: string;
  title: string;
  fields: readonly PickerField[];
  survey?: string;
}

/**
 * "3 fields hidden" beside a picker's search (SR-462): how many fields an administrator hid from
 * these sections (`CatalogueSections.hidden`), so nobody takes one for lost. An author of the rule
 * gets a link to Reports → Fields; anyone else is told an admin hid them. Nothing while none is.
 */
export function HiddenFieldsNote({ sections }: { sections: readonly IFieldSection[] & { hidden?: number } }) {
  const can = useCan();
  const hidden = sections.hidden ?? 0;
  if (hidden === 0) return null;
  const words = `${hidden} ${hidden === 1 ? 'field' : 'fields'} hidden`;
  return (
    <p className="px-1 pt-1.5 text-xs text-muted-foreground" data-hidden-fields={hidden}>
      {can('internal_fields.author') ? (
        <Link to={TEAM_PATHS.reportFields} className="underline underline-offset-2 hover:text-foreground">
          {words}
        </Link>
      ) : (
        `${words} by an admin`
      )}
    </p>
  );
}

/** Whether a field matches a search: every word of it in the field's number and label, its
 *  origin and what it pre-fills from — "brokercheck firm" — and its code, with `codes` on. */
export function matchesField(
  field: PickerField,
  needle: string,
  codes = false
): boolean {
  const words = needle.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return true;
  const text =
    `${field.number ?? ''} ${field.label} ${provenanceSearchText(field.provenance)}${codes ? ` ${field.code}` : ''}`.toLowerCase();
  return words.every((word) => text.includes(word));
}

/** Where a field's value comes from, under its name (SR-420's AC1): the origin, then its survey,
 *  cycle and what it is pre-filled or filled from. Nothing where the field has no provenance. */
export function ProvenanceLine({ field, survey, id }: { field: PickerField; survey?: string; id?: string }) {
  if (!field.provenance) return null;
  const parts = provenanceParts(field.provenance, survey);
  return (
    <span id={id} data-field-provenance={field.provenance.origin} className="block text-xs leading-4 text-muted-foreground">
      <span className="font-medium text-[var(--veritant-royal-70)]">{originLabel(field.provenance.origin)}</span>
      {parts.length > 0 && ` · ${parts.join(' · ')}`}
    </span>
  );
}

/** What the provenance line's words mean, and what filtering on a profile field rather than a
 *  survey question does (SR-420; the client's question of 13 Oct 2026) — beside Fields and Columns. */
export function FieldSourcesHelp() {
  return (
    <HelpPopover label="About where fields come from">
      <p>
        The line under each field says where its value comes from, the survey and the cycle.
      </p>
      <ul className="grid list-disc gap-1 pl-4">
        <li>
          <strong>Profile</strong> — the advisor's, team's or firm's name, firm, city and contacts. The system fills
          them from the survey questions named, whichever survey and cycle answered last, so a row may show a newer
          firm than its own response. Name is given at registration.
        </li>
        <li>
          <strong>Survey question</strong> — the answer in the row's response, the participant's latest in that
          survey; it may be last cycle's. Some open pre-filled from BrokerCheck, IAPD, SEC ADV or last cycle.
        </li>
        <li>
          <strong>Staff field</strong> — kept by the team: the same in every cycle, or one value per cycle.{' '}
          <strong>BrokerCheck</strong> — a staff field the system keeps from BrokerCheck, read-only.
        </li>
      </ul>
      <p>
        Filtering on Profile finds participants by what they are now; filtering on a Survey question finds what
        one response says. A field no survey of the participant asks is empty on its rows — Last name on Teams
        and RIA firms.
      </p>
    </HelpPopover>
  );
}

/** A profile field's sources survey by survey (SR-420's AC2), for the field the picker is on. */
function DrawsDetail({ field }: { field: PickerField }) {
  const draws = provenanceDraws(field.provenance);
  if (draws.length === 0) return null;
  return (
    <div data-field-draws={field.code} className="grid gap-0.5 border-t border-border px-3 py-2 text-xs">
      <span className="font-medium">{field.label} is filled from</span>
      {draws.map((draw) => (
        <span key={draw} className="text-muted-foreground">
          {draw}
        </span>
      ))}
    </div>
  );
}

/** A field's name in a list, its question number a muted prefix (SR-221's R2) — on one line, or
 *  with `wrap` on as many as a question's wording takes, where the list is read to choose — and
 *  with `code` on, its code a muted suffix. A field an administrator hid (SR-462), which a view
 *  still holds, says so, for its owner to replace. */
export function FieldName({
  field,
  wrap = false,
  code = false,
  id
}: {
  field: PickerField;
  wrap?: boolean;
  code?: boolean;
  id?: string;
}) {
  return (
    <span id={id} className={wrap ? 'min-w-0 break-words' : 'min-w-0 truncate'}>
      {field.number && <span className="mr-1.5 tabular-nums text-muted-foreground">{field.number}</span>}
      {field.label}
      {field.hidden && (
        <StatusChip tone="attention" className="ml-1.5 align-middle">
          Hidden by admin
        </StatusChip>
      )}
      {code && (
        <>
          {' '}
          <span className="ml-1 font-mono text-xs text-muted-foreground">{field.code}</span>
        </>
      )}
    </span>
  );
}

/**
 * Which of the catalogue's groups are open: `start` (the group of the field chosen) and those
 * unfolded since — or, while `needle` is a search, every group but those folded during it, which
 * the next search forgets.
 */
export function useFieldFolds(needle: string, start: readonly string[] = []) {
  const searching = needle.trim() !== '';
  const [open, setOpen] = React.useState<ReadonlySet<string>>(() => new Set(start));
  const [shut, setShut] = React.useState<ReadonlySet<string>>(() => new Set());
  React.useEffect(() => setShut(new Set()), [needle]);
  const isOpen = (key: string) => (searching ? !shut.has(key) : open.has(key));
  const toggle = (key: string) =>
    searching ? setShut((now) => flip(now, key)) : setOpen((now) => flip(now, key));
  return {
    searching,
    isOpen,
    toggle,
    set: (key: string, to: boolean) => {
      if (isOpen(key) !== to) toggle(key);
    }
  };
}

/** The set with `key` added, or taken out where it was in. */
function flip(set: ReadonlySet<string>, key: string): ReadonlySet<string> {
  const next = new Set(set);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** A group's heading — the fold's press, its title and its count (the matches, during a search). */
export function FieldGroupHeading({
  section,
  count,
  open,
  controls,
  onToggle,
  onKeyDown,
  item = false
}: {
  section: Pick<IFieldSection, 'key' | 'title'>;
  count: number;
  open: boolean;
  /** The id of what it folds. */
  controls: string;
  onToggle: () => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  /** One of the picker's keyboard stops. */
  item?: boolean;
}) {
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      data-field-group={section.key}
      data-open={open ? '' : undefined}
      data-picker-item={item ? '' : undefined}
      tabIndex={item ? -1 : undefined}
      onClick={onToggle}
      onKeyDown={onKeyDown}
      className="flex h-7 w-full items-center gap-1 rounded-sm px-1 text-left text-xs font-medium text-[var(--veritant-royal-70)] outline-none hover:bg-[var(--accent)] focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Chevron className="size-3.5 shrink-0" aria-hidden />
      {section.title}{' '}
      <span className="font-normal text-muted-foreground">({count.toLocaleString('en-US')})</span>
    </button>
  );
}

/**
 * One field chosen from the catalogue's groups. `value` is the field's code, `''` for none; one
 * the groups do not hold (a stored condition on a field no longer offered) is named as spelled.
 * The trigger is a `combobox` named `label`; `placeholder` is what it says with nothing chosen;
 * `codes` draws and searches each field's code beside its name.
 */
export function FieldPicker({
  label,
  sections,
  value,
  placeholder = 'Choose a field',
  onChoose,
  invalid = false,
  disabled = false,
  codes = false,
  className
}: {
  label: string;
  sections: readonly IFieldSection[];
  value: string;
  placeholder?: string;
  onChoose: (code: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  codes?: boolean;
  className?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const chosen =
    value === ''
      ? undefined
      : sections.flatMap((section) => section.fields).find((field) => field.code === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-label={label}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          className={cn(
            'flex h-8 min-w-0 items-center justify-between gap-2 rounded-md border border-input bg-white px-2.5 text-left text-[0.8125rem] outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:border-destructive',
            className
          )}
        >
          {value === '' ? (
            <span className="truncate text-muted-foreground">{placeholder}</span>
          ) : chosen ? (
            <FieldName field={chosen} code={codes} />
          ) : (
            <span className="truncate">{value}</span>
          )}
          <ChevronDown className="size-4 shrink-0 opacity-50" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label={label}
        className="w-[min(24rem,calc(100vw-2rem))] p-0 text-[0.8125rem]"
        onOpenAutoFocus={(event) => {
          // The search box takes the focus, not the content's first stop.
          event.preventDefault();
          (event.currentTarget as HTMLElement).querySelector<HTMLElement>('input[type="search"]')?.focus();
        }}
      >
        <FieldList
          sections={sections}
          value={value}
          codes={codes}
          onChoose={(code) => {
            setOpen(false);
            onChoose(code);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/** The keys that move through the list's headings and fields, and where each moves to. */
const WALK_KEYS: Readonly<Record<string, 1 | -1 | 'first' | 'last'>> = {
  ArrowDown: 1,
  ArrowUp: -1,
  Home: 'first',
  End: 'last'
};

/** The popover's body — mounted per opening, so each starts folded and with an empty search. Lists'
 *  *Filters* (SR-412) draws it too, `placeholder` its search box's. */
export function FieldList({
  sections,
  value,
  codes,
  placeholder = 'Search fields…',
  onChoose
}: {
  sections: readonly IFieldSection[];
  value: string;
  codes: boolean;
  placeholder?: string;
  onChoose: (code: string) => void;
}) {
  const [search, setSearch] = React.useState('');
  const start = sections
    .filter((section) => section.fields.some((field) => field.code === value))
    .map((section) => section.key);
  const folds = useFieldFolds(search, start);
  const list = React.useRef<HTMLDivElement>(null);
  const idBase = React.useId();
  // The profile field the pointer or the focus is on, whose sources show under the list; a new
  // search forgets it, as the field may no longer be shown.
  const [active, setActive] = React.useState<Nullable<PickerField>>(null);
  React.useEffect(() => setActive(null), [search]);
  const activate = (field: PickerField) => setActive(field.provenance?.origin === 'profile' ? field : null);

  const shown = sections
    .map((section) => ({
      section,
      fields: folds.searching ? section.fields.filter((field) => matchesField(field, search, codes)) : section.fields
    }))
    .filter(({ fields }) => !folds.searching || fields.length > 0);

  // The field chosen is brought into view as the list opens on its group.
  React.useEffect(() => {
    list.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, []);

  const stops = () => [...(list.current?.querySelectorAll<HTMLElement>('[data-picker-item]') ?? [])];
  const step = (from: HTMLElement | null, by: 1 | -1 | 'first' | 'last') => {
    const all = stops();
    if (all.length === 0) return;
    if (by === 'first') return all[0].focus();
    if (by === 'last') return all[all.length - 1].focus();
    const at = from ? all.indexOf(from) : -1;
    const next = at + by;
    if (next < 0) {
      list.current?.parentElement?.querySelector<HTMLElement>('input[type="search"]')?.focus();
      return;
    }
    all[Math.min(next, all.length - 1)]?.focus();
  };
  const walk = (event: React.KeyboardEvent<HTMLElement>) => {
    const by = WALK_KEYS[event.key];
    if (by === undefined) return false;
    event.preventDefault();
    step(event.currentTarget, by);
    return true;
  };

  return (
    <div className="grid">
      <div className="border-b border-border p-2">
        <Input
          type="search"
          aria-label="Search the fields"
          placeholder={placeholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              step(null, 'first');
            } else if (event.key === 'Enter') {
              event.preventDefault();
              const first = folds.searching
                ? shown.find(({ section }) => folds.isOpen(section.key))?.fields[0]
                : undefined;
              if (first) onChoose(first.code);
              else step(null, 'first');
            }
          }}
          className="h-8"
        />
        <HiddenFieldsNote sections={sections} />
      </div>
      <div ref={list} className="grid max-h-80 grid-cols-[minmax(0,1fr)] gap-0.5 overflow-y-auto p-1.5">
        {shown.length === 0 && (
          <p className="px-1 py-2 text-muted-foreground">No field matches the search.</p>
        )}
        {shown.map(({ section, fields }) => {
          const open = folds.isOpen(section.key);
          const id = `${idBase}-${section.key}`;
          return (
            <div key={section.key} className="grid grid-cols-[minmax(0,1fr)]">
              <FieldGroupHeading
                section={section}
                count={fields.length}
                open={open}
                controls={id}
                item
                onToggle={() => folds.toggle(section.key)}
                onKeyDown={(event) => {
                  if (walk(event)) return;
                  if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
                    event.preventDefault();
                    folds.set(section.key, event.key === 'ArrowRight');
                  }
                }}
              />
              {open && (
                <div
                  id={id}
                  role="listbox"
                  aria-label={section.title}
                  className="grid grid-cols-[minmax(0,1fr)]"
                >
                  {fields.map((field) => (
                    <div
                      key={field.code}
                      role="option"
                      aria-labelledby={`${id}-${field.code}-name`}
                      aria-describedby={field.provenance ? `${id}-${field.code}-source` : undefined}
                      aria-selected={field.code === value}
                      tabIndex={-1}
                      data-picker-item=""
                      data-field-option={field.code}
                      onClick={() => onChoose(field.code)}
                      onMouseEnter={() => activate(field)}
                      onFocus={() => activate(field)}
                      onKeyDown={(event) => {
                        if (walk(event)) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          onChoose(field.code);
                        } else if (event.key === 'ArrowLeft') {
                          event.preventDefault();
                          folds.set(section.key, false);
                          list.current
                            ?.querySelector<HTMLElement>(`[data-field-group="${CSS.escape(section.key)}"]`)
                            ?.focus();
                        }
                      }}
                      className={cn(
                        'grid min-h-7 cursor-default grid-cols-[minmax(0,1fr)] items-center rounded-sm py-1 pr-1 pl-6 outline-none hover:bg-[var(--accent)] focus-visible:bg-[var(--accent)] focus-visible:ring-2 focus-visible:ring-ring',
                        field.code === value && 'font-medium text-[var(--color-royal-blue)]'
                      )}
                    >
                      <FieldName field={field} wrap code={codes} id={`${id}-${field.code}-name`} />
                      <ProvenanceLine field={field} survey={section.survey} id={`${id}-${field.code}-source`} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {active && <DrawsDetail field={active} />}
    </div>
  );
}
